import type { EntityVersion } from '@tunasong/models'
import { dayjs, isCollabEntity } from '@tunasong/models'
import { entitiesApi } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { SyncHandler } from '@tunasong/sync-lib'
import { Button, LinearProgress, Typography } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC } from 'react'
import { Fragment, useEffect, useRef, useState } from 'react'
import styles from './history-changes.module.css'
import { collapseUnchanged, diffVersionLines, hasLineChanges } from './version-diff.js'
import type { DiffRow } from './version-diff.js'
import { isTagsFieldChange, versionFieldChanges } from './version-fields.js'
import type { FieldChange } from './version-fields.js'
import { versionLines } from './version-lines.js'

export interface HistoryChangesProps {
  entity: Persisted<Entity>
  /** The version shown */
  version: EntityVersion
  /** The version it is compared with: the one before it */
  previous: EntityVersion
  /**
   * The live content when `version` is the current state, which is not a numbered version yet. It is then compared
   * with the latest numbered version, and its fields are the entity's own.
   */
  liveContent?: unknown[]
  restoreDisabled: boolean
  onBack(): void
  /** Restore is the owner's (GH #2726): without it, the view offers none */
  onRestore?(version: EntityVersion): void
}

/** Versions are shown by their date and time in the user's locale, not by number (the user, 2026-10-25) */
const shownAt = (version: EntityVersion) => dayjs(version.versionAt).format('LLL')

const contentOf = (entityId: string, yDoc?: string | null) =>
  yDoc ? (SyncHandler.createFromYDocString({ entityId, yDoc }).toJSON() as unknown[]) : []

const fieldText = (change: FieldChange) => {
  if (isTagsFieldChange(change)) {
    const added = change.added.map(tag => `+ ${tag}`)
    const removed = change.removed.map(tag => `− ${tag}`)
    return `Tags: ${[...added, ...removed].join(' ')}`
  }
  return `${change.label}: ${change.from} → ${change.to}`
}

const DiffLine: FC<{ row: DiffRow }> = ({ row }) => {
  const glyph = row.kind === 'added' ? '+' : row.kind === 'removed' ? '−' : ' '
  const prefix = row.kind === 'added' ? 'Added: ' : row.kind === 'removed' ? 'Removed: ' : ''
  return (
    <li className={cn(styles.row, styles[row.kind], row.heading && styles.heading)}>
      <span className={styles.glyph} aria-hidden="true" data-diff-glyph="">
        {glyph}
      </span>
      <span className={styles.text}>
        {prefix ? <span className={styles.visuallyHidden}>{prefix}</span> : null}
        {row.parts
          ? row.parts.map((part, index) =>
              part.changed ? <strong key={index}>{part.text}</strong> : <Fragment key={index}>{part.text}</Fragment>
            )
          : row.text}
      </span>
    </li>
  )
}

/**
 * "Show changes" for one version in the History dialog (GH #2695, docs/ux/2026-10-24-history-show-changes.md): what
 * changed from the version before it, fields first, then content line by line. It replaces the version list, with
 * Back and Restore.
 */
export const HistoryChanges: FC<HistoryChangesProps> = props => {
  const { entity, version, previous, liveContent, restoreDisabled, onBack, onRestore } = props
  const isCurrent = Boolean(liveContent)

  const before = entitiesApi.useLoadEntityVersionQuery({ id: entity.id, version: previous.version })
  const after = entitiesApi.useLoadEntityVersionQuery({ id: entity.id, version: version.version }, { skip: isCurrent })

  const headingRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => headingRef.current?.focus(), [])

  const [expanded, setExpanded] = useState<Set<number>>(() => new Set())

  const isLoading = before.isLoading || before.isFetching || (!isCurrent && (after.isLoading || after.isFetching))
  const isError = before.isError || (!isCurrent && after.isError)
  const failedVersion = !isCurrent && after.isError ? version : previous

  const handleRetry = () => {
    if (before.isError) {
      void before.refetch()
    }
    if (!isCurrent && after.isError) {
      void after.refetch()
    }
  }

  const afterFields = isCurrent ? entity : after.data
  const ready = !isLoading && !isError && before.data && afterFields

  const rows =
    ready && before.data
      ? diffVersionLines(
          versionLines(contentOf(entity.id, before.data.yDoc)),
          versionLines(isCurrent ? liveContent : contentOf(entity.id, after.data?.yDoc))
        )
      : []
  const fields = ready && before.data && afterFields ? versionFieldChanges(before.data, afterFields) : []
  const items = collapseUnchanged(rows)
  /**
   * GH #2831: a version row points at one S3 object version of the doc, and those expire after 30 days; the server
   * then sends `yDoc: null`. For an entity with a doc that means the content is gone, not that it is empty, so there
   * is nothing to compare and nothing to restore.
   */
  const hasDoc = isCollabEntity(entity)
  const thisContentGone = hasDoc && !isCurrent && after.data?.yDoc === null
  const contentGone = Boolean(ready) && hasDoc && (before.data?.yDoc === null || thisContentGone)
  const showDiff = Boolean(ready) && !contentGone
  const hasChanges = hasLineChanges(rows) || fields.length > 0

  const toggle = (index: number) =>
    setExpanded(current => {
      const next = new Set(current)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <Typography variant="subtitle1" component="h3" ref={headingRef} tabIndex={-1} className={styles.title}>
          {shownAt(version)}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Changes since {shownAt(previous)}
        </Typography>
      </header>
      {isLoading ? <LinearProgress /> : null}

      <div className={styles.body}>
        {isError ? (
          <div className={styles.message} role="status">
            <Typography variant="body2">Couldn&apos;t load the version from {shownAt(failedVersion)}.</Typography>
            <Button onClick={handleRetry}>Try again</Button>
          </div>
        ) : null}

        {contentGone ? (
          <Typography variant="body2" className={styles.message}>
            This version&apos;s content is no longer available
          </Typography>
        ) : null}

        {showDiff && !hasChanges ? (
          <Typography variant="body2" className={styles.message}>
            No changes to the content or details in this version.
          </Typography>
        ) : null}

        {showDiff && fields.length > 0 ? (
          <section aria-labelledby="history-changes-details" className={styles.details}>
            <Typography variant="subtitle2" component="h4" id="history-changes-details">
              Details
            </Typography>
            <ul className={styles.fieldList}>
              {fields.map(change => (
                <li key={change.label}>{fieldText(change)}</li>
              ))}
            </ul>
          </section>
        ) : null}

        {showDiff && hasLineChanges(rows) ? (
          <ul className={styles.lines}>
            {items.map((item, index) =>
              item.kind === 'collapsed' ? (
                <Fragment key={index}>
                  <li className={styles.collapsed}>
                    <button
                      type="button"
                      className={styles.collapsedButton}
                      aria-expanded={expanded.has(index)}
                      onClick={() => toggle(index)}
                    >
                      {item.rows.length} unchanged lines
                    </button>
                  </li>
                  {expanded.has(index) ? item.rows.map((row, r) => <DiffLine key={`${index}-${r}`} row={row} />) : null}
                </Fragment>
              ) : (
                <DiffLine key={index} row={item} />
              )
            )}
          </ul>
        ) : null}
      </div>

      <footer className={styles.footer}>
        <Button onClick={onBack}>Back to versions</Button>
        {onRestore ? (
          <Button color="primary" onClick={() => onRestore(version)} disabled={restoreDisabled || thisContentGone}>
            Restore this version
          </Button>
        ) : null}
      </footer>
    </div>
  )
}

export default HistoryChanges
